import React, { useCallback, useEffect, useState } from 'react'
import Alert from '@mui/material/Alert'
import Typography from '@mui/material/Typography'
import DialogConfirmCancel from '../../../../dialog/DialogConfirm'
import useUsers from '../../../../../hooks/users/useUsers'
import { useNavigate } from 'react-router-dom'
import CkeckboxRolesSkeleton from '../../../../skeleton/CkeckboxRolesSkeleton'
import { Checkbox, FormControlLabel, ToggleButton, ToggleButtonGroup } from '@mui/material'
import { getRoles } from '../../../../../services/roles/roles.service'
import TextEllipsis from '../../../../text/TextEllipsis'
import { handlerErrorV2 } from '../../../../../utils/errors'

const DialogAction = ({ show, onClose, type, form, onReset }) => {
  const types = [
    'archive',
    'delete',
    'send invitation',
    'recovery password',
    'bulk send invitation',
    'bulk recovery password',
    'login impersonate',
    'batch update roles'
  ]
  const navigate = useNavigate()

  const [loading, toggleLoading] = useState(false)
  const [errors, setInputErrors] = useState({})

  const [roles, setRoles] = useState([])
  const [loadingRoles, setLoadingRoles] = useState(false)
  const [formData, setForm] = useState(form)
  // MF-579 — a batch adds roles or removes roles; it never replaces what the users already have
  const [mode, setMode] = useState('add')
  const {
    deleteUser,
    archiveUser,
    invite,
    resetPassword,
    bulkInvite,
    bulkResetPassword,
    loginImpersonate,
    updateBatchRoles
  } = useUsers()

  const onChange = (name, checked) => {
    let rolesId = new Set(formData?.rolesId || [])
    if (checked) {
      rolesId.add(name)
    } else {
      rolesId.delete(name)
    }
    rolesId = [...rolesId]
    const rolesd = roles?.filter((role) => rolesId.includes(role.id)) || []
    setForm({ ...form, rolesId: [...rolesId], roles: rolesd })
  }

  const onConfirm = async (e) => {
    if (!types.includes(type)) {
      setInputErrors({ message: 'Invalid type' })
      return
    }
    e.preventDefault()
    toggleLoading(true)
    try {
      let result = { error: false }
      if (type === 'delete') {
        result = await deleteUser(form._id)
      }
      if (type === 'archive') {
        result = await archiveUser(form._id)
      }
      if (type === 'send invitation') {
        result = await invite(form._id, form.email)
      }
      if (type === 'recovery password') {
        result = await resetPassword(form._id, form.email)
      }
      if (type === 'bulk send invitation') {
        result = await bulkInvite(form._id)
      }
      if (type === 'bulk recovery password') {
        result = await bulkResetPassword(form._id)
      }
      if (type === 'login impersonate') {
        result = await loginImpersonate(form._id)
      }
      if (type === 'batch update roles') {
        if (!formData?.rolesId?.length) {
          setInputErrors({ message: 'Select at least one role' })
          return
        }
        result = await updateBatchRoles(form._id, formData, mode)
      }
      if (result.error) {
        setInputErrors(result)
      } else {
        if (type === 'login impersonate') {
          const { charts, userRoles } = result
          const userRoleIds = (userRoles || []).map((r) => String(r._id || r))
          const isParent = (userRoles || []).some((r) =>
            (r.name || r.code || '').toLowerCase().includes('parent')
          )
          const defaultChart =
            !isParent &&
            charts?.find((c) => c.defaultForRole?.some((roleId) => userRoleIds.includes(String(roleId))))
          navigate(defaultChart ? `/dashboard/analytics/${defaultChart.code}` : '/dashboard')
        } else {
          navigate('/dashboard/users')
        }
        onReset()
        onClose()
      }
    } finally {
      toggleLoading(false)
    }
  }

  const loadRoles = useCallback(async () => {
    setLoadingRoles(true)
    try {
      const response = await getRoles()
      if (response.error) {
        setInputErrors({ message: response.error.message || 'An error occurred' })
      } else {
        setRoles(response.data.filter((role) => !['Student', 'Parent'].includes(role.id)) || [])
      }
    } catch (error) {
      const message = handlerErrorV2(error)
      setInputErrors({ message })
    } finally {
      setLoadingRoles(false)
    }
  }, [])

  useEffect(() => {
    if (type === 'batch update roles') {
      loadRoles()
    }
  }, [type, loadRoles])

  return (
    <DialogConfirmCancel
      id={`user-${type}-${Array.isArray(form._id) ? form._id.join('-') : form._id}`}
      open={show}
      onCancel={onClose}
      onConfirm={types.includes(type) ? onConfirm : undefined}
      loading={loading}
      title={type}
      onCloseHeader={true}
      maxWidth='xs'
    >
      {types.includes(type) ? (
        <>
          {type.indexOf('bulk') > -1 ? (
            <>
              {type === 'bulk send invitation' && (
                <Typography component={'div'}>
                  Are you sure you would like to invite {form?._id?.length || 0} users to join Maestro?
                </Typography>
              )}
              {type === 'bulk recovery password' && (
                <Typography component={'div'}>
                  Are you sure you would like to send {form?._id?.length || 0} users an email to reset their
                  password?
                </Typography>
              )}
            </>
          ) : type === 'batch update roles' ? (
            <>
              <ToggleButtonGroup
                size='small'
                exclusive
                fullWidth
                value={mode}
                onChange={(_, value) => value && setMode(value)}
                sx={{ mb: 1 }}
              >
                <ToggleButton value='add' sx={{ textTransform: 'none' }}>
                  Add roles
                </ToggleButton>
                <ToggleButton value='remove' sx={{ textTransform: 'none' }}>
                  Remove roles
                </ToggleButton>
              </ToggleButtonGroup>
              <Typography component={'div'} variant='body2' sx={{ mb: 1 }}>
                {mode === 'add' ? 'Adds' : 'Removes'}{' '}
                <b>{(formData?.roles || []).map((role) => role.name).join(', ') || 'the roles you tick'}</b>{' '}
                {mode === 'add' ? 'to' : 'from'} {form?._id?.length || 0} selected users. Their other roles
                are not touched.
              </Typography>
              <div className='col-span-3 grid grid-cols-3 gap-1'>
                {loadingRoles ? (
                  <CkeckboxRolesSkeleton />
                ) : (
                  roles?.map((role, index) => (
                    <div key={`role-${index}-${role._id}`} className='overflow-hidden'>
                      <FormControlLabel
                        control={
                          <Checkbox
                            id={`checkbox-roles-${index}-${role._id}`}
                            checked={formData?.rolesId?.includes(role.id) ? true : false}
                            name={role._id}
                            onChange={(e) => onChange(role.id, e.target.checked)}
                            color='primary'
                          />
                        }
                        label={
                          <TextEllipsis id={`text-roles-${index}-${role._id}`}>{role.name}</TextEllipsis>
                        }
                      />
                    </div>
                  ))
                )}
              </div>
            </>
          ) : (
            <Typography component={'div'}>
              Are you sure you want to {type} the user: <b>{form.fullName}</b>?
            </Typography>
          )}
        </>
      ) : (
        <Alert severity='error'>Invalid type</Alert>
      )}
      {errors?.message && <Alert severity='error'>{errors?.message}</Alert>}
    </DialogConfirmCancel>
  )
}

export default DialogAction
